import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Building2, ArrowRight } from "lucide-react";

export default function Login() {
    const { login, user } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [demoUsers, setDemoUsers] = useState([]);

    useEffect(() => {
        if (user) nav("/");
        api.get("/auth/demo-users").then((r) => setDemoUsers(r.data)).catch(() => {});
    }, [user, nav]);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Authenticated. Welcome to Command Center.");
            nav("/");
        } catch (err) {
            toast.error("Invalid credentials");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[var(--bg-base)] text-[var(--text-primary)] flex relative overflow-hidden grain">
            {/* Left panel with imagery */}
            <div
                className="hidden lg:flex flex-1 relative bg-cover bg-center"
                style={{ backgroundImage: "url('https://images.unsplash.com/photo-1693661391267-ad955aeeb564?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwbmlnaHQlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzgyOTkyMTY2fDA&ixlib=rb-4.1.0&q=85')" }}
            >
                <div className="absolute inset-0 bg-[rgba(15,23,42,0.75)]" />
                <div className="relative z-10 p-12 flex flex-col justify-between w-full text-slate-100">
                    <div>
                        <div className="flex items-center gap-3 mb-16">
                            <div className="w-10 h-10 bg-[var(--brand-amber)] flex items-center justify-center">
                <Building2 size={22} className="text-white" strokeWidth={2.5} />
                            </div>
                            <div>
                                <div className="font-display font-black text-xl leading-none">A W CONSTRUCTIONS</div>
                                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mt-1">Private Limited · Bhopal</div>
                            </div>
                        </div>
                        <div className="max-w-xl">
                            <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-4">CFO Command Center · v1.0</div>
                            <h1 className="font-display font-black text-4xl xl:text-6xl leading-[0.95] tracking-tight">
                                From <span className="text-[var(--brand-amber)]">₹15 Cr</span> to <span className="text-[var(--brand-amber)]">₹100 Cr</span>.
                                <br />One control tower.
                            </h1>
                            <p className="mt-6 font-body text-sm text-slate-300 max-w-md leading-relaxed">
                                Every rupee. Every project. Every risk. Real-time visibility across finance, projects, procurement, HR, compliance & AI-powered strategy — engineered for the Managing Director's desk.
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-4 gap-6 font-mono-fin border-t border-white/10 pt-6">
                        <div>
                            <div className="text-[9px] uppercase tracking-[0.2em] text-slate-300">Modules</div>
                            <div className="font-display text-2xl font-black text-slate-100 mt-1">16</div>
                        </div>
                        <div>
                            <div className="text-[9px] uppercase tracking-[0.2em] text-slate-300">KPIs Tracked</div>
                            <div className="font-display text-2xl font-black text-slate-100 mt-1">140+</div>
                        </div>
                        <div>
                            <div className="text-[9px] uppercase tracking-[0.2em] text-slate-300">Ratios</div>
                            <div className="font-display text-2xl font-black text-slate-100 mt-1">22</div>
                        </div>
                        <div>
                            <div className="text-[9px] uppercase tracking-[0.2em] text-slate-300">AI Engine</div>
                            <div className="font-display text-2xl font-black text-[var(--brand-amber)] mt-1">Claude 4.5</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right panel - login form */}
            <div className="w-full lg:w-[480px] flex items-center justify-center p-6 relative z-10 bg-[var(--bg-base)]">
                <div className="w-full max-w-sm">
                    <div className="lg:hidden flex items-center gap-2 mb-8">
                        <div className="w-8 h-8 bg-[var(--brand-amber)] flex items-center justify-center">
                            <Building2 size={16} className="text-black" strokeWidth={2.5} />
                        </div>
                        <div className="font-display font-black text-[var(--text-primary)] text-sm">A W CONSTRUCTIONS</div>
                    </div>

                    <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-3">// Authenticate</div>
                    <h2 className="font-display font-black text-3xl tracking-tight">Executive Sign In</h2>
                    <p className="mt-2 font-body text-sm text-[var(--text-secondary)]">Role-based access · Terminal-grade security</p>

                    <form onSubmit={submit} className="mt-8 space-y-4">
                        <div>
                            <label className="block text-[10px] font-mono-fin uppercase tracking-[0.15em] text-[var(--text-secondary)] mb-2">Email</label>
                            <input
                                data-testid="login-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-[var(--bg-panel)] border border-panel px-3 py-2.5 text-sm font-mono-fin focus:border-[var(--brand-amber)] focus:outline-none"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-mono-fin uppercase tracking-[0.15em] text-[var(--text-secondary)] mb-2">Password</label>
                            <input
                                data-testid="login-password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full bg-[var(--bg-panel)] border border-panel px-3 py-2.5 text-sm font-mono-fin focus:border-[var(--brand-amber)] focus:outline-none"
                                required
                            />
                        </div>
                        <button
                            data-testid="login-submit"
                            type="submit"
                            disabled={loading}
                            className="w-full bg-[var(--brand-amber)] text-black font-mono-fin uppercase tracking-[0.15em] text-xs py-3 hover:bg-[var(--bg-panel-hover)] transition-colors disabled:opacity-50 flex items-center justify-center gap-2 font-semibold"
                        >
                            {loading ? "Authenticating..." : <>Enter Command Center <ArrowRight size={14} /></>}
                        </button>
                    </form>

                    {demoUsers.length > 0 && (
                        <div className="mt-8 border-t border-panel pt-6">
                            <div className="text-[10px] font-mono-fin uppercase tracking-[0.15em] text-[var(--text-secondary)] mb-3">Leadership · Quick Sign-in</div>
                            <div className="grid grid-cols-2 gap-2">
                                {demoUsers.map((u) => (
                                    <button
                                        key={u.email}
                                        data-testid={`demo-user-${u.role.toLowerCase()}`}
                                        onClick={() => setEmail(u.email)}
                                        className="border border-panel p-2 text-left hover:border-[var(--brand-amber)] transition-colors"
                                    >
                                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--brand-amber)]">{u.designation}</div>
                                        <div className="text-[10px] font-mono-fin text-[var(--text-primary)] mt-0.5 truncate">{u.name}</div>
                                    </button>
                                ))}
                            </div>
                            <div className="mt-4 text-[10px] font-mono-fin text-[var(--text-muted)]">Click a leader above to fill the email field · enter your password to sign in.</div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
